import { Button as HeroButton, type ButtonProps as HeroButtonProps } from '@heroui/react';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import { useDebouncedLoader } from 'use-debounced-loader';

import DynamicT from '@/shared/components/DynamicT';
import MatrixLoader from '@/shared/components/MatrixLoader';

/** `secondary` is an alternative action (Download, Copy); `tertiary` dismisses (Cancel, Skip). */
export type ButtonType = 'primary' | 'secondary' | 'tertiary' | 'danger';

type BaseProps = Omit<
  HeroButtonProps,
  'type' | 'size' | 'variant' | 'children' | 'className' | 'isDisabled' | 'isPending' | 'onClick'
> & {
  readonly htmlType?: 'button' | 'submit' | 'reset';
  readonly type?: ButtonType;
  readonly size?: 'small' | 'large';
  readonly isDisabled?: boolean;
  readonly isLoading?: boolean;
  readonly className?: string;
  readonly onClick?: () => void;
};

type Props = BaseProps & {
  readonly title: TFuncKey;
  readonly icon?: React.ReactNode;
  readonly i18nProps?: Record<string, unknown>;
};

const variantMap = {
  primary: 'primary',
  secondary: 'secondary',
  tertiary: 'tertiary',
  danger: 'danger-soft',
} as const satisfies Record<ButtonType, HeroButtonProps['variant']>;

/** A pending button keeps its full appearance; the spinner is signal enough. */
export const pendingKeepsAppearanceClass = 'data-[pending=true]:opacity-100';

const Button = ({
  htmlType = 'button',
  type = 'primary',
  size = 'large',
  title,
  i18nProps,
  className,
  isDisabled = false,
  isLoading = false,
  icon,
  onClick,
  ...rest
}: Props) => {
  // Pending guards the click immediately; the spinner waits so instant responses don't flash.
  const isSpinnerVisible = useDebouncedLoader(isLoading, 100);
  // A full-width label has room beside it, so the spinner overlays the start edge instead of
  // pushing the centered label sideways. The padding that keeps a long label clear of it
  // applies only while it shows: held always, it pushed a two-button row past a 320px screen.
  const isSpinnerOverlaid = size === 'large' && !icon;

  return (
    <HeroButton
      type={htmlType}
      variant={variantMap[type]}
      size={size === 'large' ? 'lg' : 'md'}
      fullWidth={size === 'large'}
      isDisabled={isDisabled}
      isPending={isLoading}
      className={classNames(
        pendingKeepsAppearanceClass,
        'min-w-0',
        // A label can carry an identifier ("Continue as …"), so it wraps instead of cutting it.
        size === 'large' && 'h-auto min-h-11 py-2 whitespace-normal md:min-h-10',
        isSpinnerOverlaid && isSpinnerVisible && 'px-10',
        className
      )}
      onPress={onClick}
      {...rest}
    >
      {isSpinnerOverlaid && isSpinnerVisible && (
        <span className="absolute inset-y-0 inset-s-4 flex items-center animate-in fade-in zoom-in-95 duration-(--duration-quick) ease-(--ease-out-strong)">
          <MatrixLoader size="sm" />
        </span>
      )}
      {!isSpinnerOverlaid && (isSpinnerVisible ? <MatrixLoader size="sm" /> : icon)}
      <span
        className={classNames(
          'min-w-0',
          size === 'large' ? 'line-clamp-2 text-center text-balance' : 'truncate'
        )}
      >
        <DynamicT forKey={title} interpolation={i18nProps} />
      </span>
    </HeroButton>
  );
};

export default Button;
